import CardContainer from '../components/cardContainer';
import Card from '../components/card';
import {
    makeStyles 
} from '@mui/styles';
import {
    Box, Slide 
} from '@mui/material';
import {
    useEffect, useRef, useState 
} from 'react';
import {
    useCookies 
} from 'react-cookie';
import {
    useRouter 
} from 'next/router';
import {
    gaEvent 
} from '../../../lib/gtag';
import {
    ampLogEvent 
} from '../../../lib/amplitude';
import {
    postCheckoutInfo, postCheckoutSession 
} from '../../../logic/pricing';
import Stripe from '../../../lib/stripe';
import {
    getCheckoutConfirmPageUrl 
} from '../../../pages/checkout-success';
import {
    compose 
} from 'underscore';
import withAuthentication from '../../authentication/withauthentication';
import {
    Data, get 
} from '../../../networking';
import Loading from '../../core/loading';
import {
    GetServerSidePropsContext 
} from 'next';

async function getCouponInfo(id?: string, context?: GetServerSidePropsContext): Promise<Data> {
    if (!id) {
        return;
    }
    return await get(`payments-gateway/stripe/coupon/${id}`, {}, context);
}

async function getPrices(context?: GetServerSidePropsContext): Promise<Data> {
    return await get('payments-gateway/stripe/prices/joint_premium_checkout_20241022', {}, context);
}

const useStyles = makeStyles(() => ({
    scrollableContainer: {
        overflow: 'scroll',
        scrollbarWidth: 'none',
        minHeight: '100%',
        width: '100vw',
        maxWidth: '692px',
        transform: 'translate(-24px, 0)',
        padding: '0 24px'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    title: {
        width: '100%',
        fontFamily: 'Poppins',
        fontSize: '24px',
        lineHeight: '32px',
        fontWeight: 700,
        color: '#333',
        textAlign: 'center',
        margin: '0 0 8px',
        letterSpacing: 'normal'
    },
    subtitle: {
        fontFamily: 'Poppins',
        fontSize: '16px',
        lineHeight: '24px',
        color: '#333',
        textAlign: 'center',
        margin: '0 0 44px'
    },
    label: {
        position: 'absolute',
        fontFamily: 'Poppins',
        fontSize: '12px',
        lineHeight: '16px',
        fontWeight: 600,
        color: 'white',
        backgroundColor: '#FFE635',
        padding: '4px 12px',
        width: 'fit-content',
        borderRadius: '4px',
        transform: 'translate(0, -36px)',
        margin: 0
    },
    planInformation: {
        marginBottom: '16px'
    },
    priceBoxes: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        columnGap: '16px',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 -8px 32px',
        cursor: 'pointer'
    },
    priceBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        borderRadius: '16px',
        padding: '24px 16px',
        transition: 'background-color 0.25s 0s, border 0.25s 0s, padding 0.25s 0s',
    },
    planTitle: {
        fontFamily: 'Poppins',
        fontSize: '20px',
        lineHeight: '28px',
        fontWeight: 700,
        color: '#333',
        margin: 0,
        marginBottom: '4px'
    },
    planSubtitle: {
        fontFamily: 'Poppins',
        fontSize: '14px',
        lineHeight: '20px',
        textAlign: 'center',
        color: '#333',
        margin: 0,
        marginBottom: '12px'
    },
    planPrice: {
        fontFamily: 'Poppins',
        fontSize: '18px',
        lineHeight: '24px',
        color: '#333',
        margin: 0
    },
    featureContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '0 24px',
        alignItems: 'center'
    },
    planFeatures: {
        position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        rowGap: '16px',
        transition: 'opacity 0.5s 0s'
    },
    feature: {
        display: 'grid',
        gridTemplateColumns: '16px 1fr',
        gridGap: '8px',
    },
    featureLabel: {
        fontFamily: 'Poppins',
        fontSize: '12px',
        lineHeight: '16px',
        color: '#333',
        margin: 0,
        transition: 'opacity 0.5s 0s'
    }
}));

export const JointPremiumCheckout = (): JSX.Element => {
    const classes = useStyles();

    const [loading, setLoading] = useState(true);
    const [isPlusSelected, setIsPlusSelected] = useState(true);
    const [pricingData, setPricingData] = useState(null);
    const [cookies] = useCookies(['_fbc', '_fbp', 'ct', 'td']);
    const router = useRouter();
    const {
        ct, pg, c, td, tr, checkoutType 
    } = router.query;

    const plusPrice = useRef(null);
    const plusFeatures = useRef(null);
    const [priceBoxesHeight, setPriceBoxesHeight] = useState(null);
    const [plusFeaturesWidth, setPlusFeaturesWidth] = useState(null);

    const [confirmPageUrl, setConfirmPageUrl] = useState('');
    const [isGeneratingCheckout, setIsGeneratingCheckout] = useState(false);
    const upgradeCheckoutTypes = ['iu', 'sp'];

    const [couponResponse, setCouponResponse] = useState<Data | null>(null);

    const onClick = () => {
        postNewCheckoutSession(isPlusSelected);
    };

    const handleActiveNonStripeUsers = () => {
        router.push('/dashboard');
    };

    const postNewCheckoutSession = async (isPlusSelected: boolean) => {
        /* Prevent multiple requests */
        if (isGeneratingCheckout) return;
        try {
            setIsGeneratingCheckout(true);
            const priceId = isPlusSelected ? pricingData.prices[1].id : pricingData.prices[0].id;
            const successUrl = isPlusSelected ? confirmPageUrl : encodeURIComponent(window
                ? window.location.protocol + '//' + window.location.host + getCheckoutConfirmPageUrl({
                    redirect: '/premium-coaching/upgrade-confirmed'
                })
                : ''
            );
            const payload: any = {
                priceId,
                successUrl,
                cancelUrl: window ? window.location.href : '',
            };
            if (!isPlusSelected) payload.checkoutType = 'pc';
            const session = await postCheckoutSession(payload);

            const stripe = await Stripe;
            if (session.url) {
                router.push(session.url);
            } else if (session.id) {
                stripe.redirectToCheckout({
                    sessionId: session.id as string,
                });
            } else if (session.statusCode === 409) {
                handleActiveNonStripeUsers();
            }
        } catch (e) {
            setIsGeneratingCheckout(false);
        }
    };

    useEffect(() => {
        if (!pricingData) return;
        if (pricingData.checkout_info.checkout_type === 'ptcc') {
            setConfirmPageUrl(
                encodeURIComponent(
                    window ?
                        window.location.protocol + '//' + window.location.host + getCheckoutConfirmPageUrl({
                            redirect: '/start/confirm'
                        })
                        :
                        ''));
        } else if (upgradeCheckoutTypes.includes(pricingData.checkout_info.checkout_type)) {
            setConfirmPageUrl(
                encodeURIComponent(
                    window ?
                        window.location.protocol + '//' + window.location.host + getCheckoutConfirmPageUrl({
                            url: '/subscription/confirm-upgrade'
                        })
                        :
                        ''));
        } else {
            setConfirmPageUrl(
                encodeURIComponent(
                    window ?
                        window.location.protocol + '//' + window.location.host + getCheckoutConfirmPageUrl()
                        :
                        ''));
        }
    }, [pricingData]);

    useEffect(() => {
        gaEvent({
            action: 'Page',
            category: 'Land',
            label: 'signup: landed on checkout - your recommendation',
            value: '',
        });
        const retrievePrices = async () => {
            const pricingData = await getPrices();
            setPricingData(pricingData);
        };
        retrievePrices();

        setLoading(false);
    }, []);

    useEffect(() => {
        if (plusPrice.current) {
            setPriceBoxesHeight(plusPrice.current.clientHeight);
        }
    }, [plusPrice.current]);

    useEffect(() => {
        if (plusFeatures.current) {
            setPlusFeaturesWidth(plusFeatures.current.clientWidth);
        }
    }, [plusFeatures.current]);

    useEffect(() => {
        async function getCoupon() {
            const couponResponse = await getCouponInfo(cookies.ss_discount);
            setCouponResponse(couponResponse);
        }
        getCoupon();
    }, [cookies.ss_discount]);

    useEffect(() => {
        if (cookies.ct === 'no_cc') router.push('/subscription/no-cc');
    }, [cookies]);

    const postCheckoutData = async (payload) => {
        await postCheckoutInfo(payload);
    };

    useEffect(() => {
        const payload = {
            checkout_type: ct && ct || checkoutType && checkoutType,
            price_group: isPlusSelected ? (pg && pg || 'base_99') : 'prem_298',
            coupons: c && c,
            trial_days: td && parseInt(td as string),
            tier: isPlusSelected ? (tr && tr || null) : 'PREMIUM_COACHING',
        };
        postCheckoutData(payload);
    }, [isPlusSelected]);

    const onClickPlusPrice = () => {
        setIsPlusSelected(true);
        ampLogEvent(
            'signup: tapped to toggle between plans',
            {
                tileClicked: 'base',
            },
            cookies
        );
    };

    const onClickPremiumPrice = () => {
        setIsPlusSelected(false);
        ampLogEvent(
            'signup: tapped to toggle between plans',
            {
                tileClicked: 'premium',
            },
            cookies
        );
    };

    if (cookies.ct === 'no_cc' || loading || !pricingData) {
        return (
            <Box>
                <Loading />
            </Box>
        );
    }

    return (
        <CardContainer
            sectionKey={'checkout'}
            goNext={onClick}
            hideArrowButton
            showCTAButton
            hasGradient
            buttonText={'Try Free & Subscribe'}
            ctaText={`Try 15 days free, then ${pricingData.computed_price_list[isPlusSelected ? 1 : 0].symbol}${pricingData.computed_price_list[isPlusSelected ? 1 : 0].plan_price_with_discount.toFixed(2)}/year. Cancel any time.`}
        >
            <Card>
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={'left'}
                        timeout={250}
                        mountOnEnter
                    >
                        <Box className={classes.content}>
                            <p
                                className={classes.title}
                                dangerouslySetInnerHTML={{
                                    __html: 'Confirm Your Membership' 
                                }}
                            />
                            <p
                                className={classes.subtitle}
                                dangerouslySetInnerHTML={{
                                    __html: 'Try Sunnyside for 15 days free' 
                                }}
                            />
                            <Box
                                className={classes.priceBoxes}
                                style={{
                                    height: `${priceBoxesHeight}px` 
                                }}
                            >
                                <Box
                                    className={classes.priceBox}
                                    ref={plusPrice}
                                    style={{
                                        backgroundColor: isPlusSelected ? '#CDEBFF' : '#EFF9FF',
                                        border: isPlusSelected ? '2px solid #5DC0FE' : 'none',
                                        padding: isPlusSelected ? '24px 16px' : '16px 8px' 
                                    }}
                                    onClick={onClickPlusPrice}
                                >
                                    <p
                                        className={classes.label}
                                        style={{
                                            backgroundColor: isPlusSelected ? '#5DC0FE' : '#B4E2FF' 
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: couponResponse?.percent_off ? `${couponResponse.percent_off}% DISCOUNT APPLIED` : 'Lowest Price' 
                                        }}
                                    />
                                    <p
                                        className={classes.planTitle}
                                        dangerouslySetInnerHTML={{
                                            __html: 'Plus' 
                                        }}
                                    />
                                    <p
                                        className={classes.planSubtitle}
                                        dangerouslySetInnerHTML={{
                                            __html: 'Self-guided with light coaching' 
                                        }}
                                    />
                                    <p
                                        className={classes.planPrice}
                                        dangerouslySetInnerHTML={{
                                            __html: `<span style="font-size:20px;line-height:28px;">${pricingData.computed_price_list[1].symbol}${pricingData.computed_price_list[1].plan_price_per_month_with_discount.toFixed(2)}</span>/mo` 
                                        }}
                                    />
                                </Box>
                                <Box
                                    className={classes.priceBox}
                                    style={{
                                        backgroundColor: isPlusSelected ? '#F7EFFE' : '#E7CDFB',
                                        border: isPlusSelected ? 'none' : '2px solid #B05DF1',
                                        padding: isPlusSelected ? '16px 8px' : '24px 16px' 
                                    }}
                                    onClick={onClickPremiumPrice}
                                >
                                    <p
                                        className={classes.label}
                                        style={{
                                            backgroundColor: isPlusSelected ? '#DBB4F9' : '#B05DF1' 
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: couponResponse?.percent_off ? `${couponResponse.percent_off}% DISCOUNT APPLIED` : 'Most Support' 
                                        }}
                                    />
                                    <p
                                        className={classes.planTitle}
                                        dangerouslySetInnerHTML={{
                                            __html: 'Premium' 
                                        }}
                                    />
                                    <p
                                        className={classes.planSubtitle}
                                        dangerouslySetInnerHTML={{
                                            __html: 'Hands on human coaching' 
                                        }}
                                    />
                                    <p
                                        className={classes.planPrice}
                                        dangerouslySetInnerHTML={{
                                            __html: `<span style="font-size:20px;line-height:28px;">${pricingData.computed_price_list[0].symbol}${pricingData.computed_price_list[0].plan_price_per_month_with_discount.toFixed(2)}</span>/mo` 
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Box className={classes.featureContainer}>
                                <Box
                                    className={classes.planFeatures}
                                    ref={plusFeatures}
                                    style={{
                                        opacity: isPlusSelected ? 1 : 0 
                                    }}
                                >
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: '<span style="font-weight:700;">Science-backed</span> habit change system' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: '<span style="font-weight:700;">Tools</span> for setting and reaching your goals' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: 'Drink tracking and <span style="font-weight:700;">accountability</span> nudges' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: 'Personalized <span style="font-weight:700;">insights and analytics</span>' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: 'Support from our member <span style="font-weight:700;">community</span>' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: '<span style="font-weight:700;">Human coaching</span> when you need it' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: '<span style="font-weight:700;">Results in 30 days</span> or your money back' 
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    className={classes.planFeatures}
                                    style={{
                                        opacity: isPlusSelected ? 0 : 1,
                                        width: `${plusFeaturesWidth}px` 
                                    }}
                                >
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: 'All the great features in <span style="font-weight:700;">Plus, and more!</span>' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: '<span style="font-weight:700;">Hands on support</span> from a human coach' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: '<span style="font-weight:700;">Tailored feedback</span> based on progress' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: '<span style="font-weight:700;">Personalized coaching</span> preferences' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: '<span style="font-weight:700;">Fastest response</span> times 7 days a week' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: 'Weekly <span style="font-weight:700;">group video calls</span>' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                style={{
                                                    transition: 'fill 0.25s 0s' 
                                                }}
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={isPlusSelected ? '#5DC0FE' : '#B05DF1'}
                                            />
                                        </svg>
                                        <p
                                            className={classes.featureLabel}
                                            dangerouslySetInnerHTML={{
                                                __html: '<span style="font-weight:700;">Results in 30 days</span> or your money back' 
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                style={{
                                    height: '500px' 
                                }}
                            />
                        </Box>
                    </Slide>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default compose(withAuthentication)(JointPremiumCheckout);
